import { Message } from '@alifd/next';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Box, Typography } from '@mui/material';
import {
  Button,
  Form,
  InputNumber,
  Radio,
  Space,
  Switch,
  Tabs,
  TimePicker,
} from 'antd';
import axios from 'axios';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';

import client from '@/lib/feathers';
import type { ChargingPileElectricityRate } from '@/types/database/chargingPile/chargingPileElectricityRate';

import RangeSetting from './components/RangeSetting';
import SummerRange from './components/SummerRange';

const { TabPane } = Tabs;
const { Item, useForm } = Form;

const SettingForm = ({ handleClose, setTrigger }) => {
  const [form] = useForm();
  const selectedValue = Form.useWatch('chargeLimitMode', form); // 監聽 radioGroup
  const [initialValues, setInitialValues] =
    useState<ChargingPileElectricityRate>();
  const [summerTime, setSummerTime] = useState<[string, string]>([
    '06-15',
    '09-30',
  ]);
  const [summerPeakHours, setSummerPeakHours] = useState<[string, string][]>(
    []
  );
  // const [periodLimitSetting, setPeriodLimitSetting] = useState<
  //   [string, string][]
  // >([]);
  const [nonSummerPeakHours, setNonSummerPeakHours] = useState<
    [string, string][]
  >([]);
  // const [isSetTimeLimit, setIsSetTimeLimit] = useState(false);

  useEffect(() => {
    const getPriceSettingInitialValues = async () => {
      try {
        const { data } = await client
          .service('chargingPileElectricityRate')
          .find({});
        setInitialValues(data[0] || {});
        setSummerPeakHours(data[0]?.summer?.peak?.hours || []);
        setNonSummerPeakHours(data[0]?.nonSummer?.peak?.hours || []);
        setSummerTime((prev) => data[0]?.summer?.dates[0] || prev);
        // setIsSetTimeLimit(data[0]?.isSetTimeLimit || false);
        form.setFieldsValue({
          ...data[0],
          chargingTimeLimitResetTime: moment(
            data[0].chargingTimeLimitResetTime,
            'HH:mm'
          ),
          periodLimitSetting: data[0].periodLimitSetting.map((item) => {
            return {
              ...item,
              chargingStartTime: moment(item.chargingStartTime, 'HH:mm'),
              chargingEndTime: moment(item.chargingEndTime, 'HH:mm'),
            };
          }),
        });
      } catch (e) {
        console.error(e);
      }
    };
    getPriceSettingInitialValues();
  }, [form]);

  const handleSubmit = async (values: ChargingPileElectricityRate) => {
    const patchValues: ChargingPileElectricityRate = JSON.parse(
      JSON.stringify(values)
    );

    patchValues.chargingTimeLimitResetTime = moment(
      values.chargingTimeLimitResetTime
    ).format('HH:mm');
    patchValues.chargingStartTime = moment(values.chargingStartTime).format(
      'HH:mm'
    );
    patchValues.chargingEndTime = moment(values.chargingEndTime).format(
      'HH:mm'
    );
    if (summerPeakHours.length === 0 || nonSummerPeakHours.length === 0) {
      Message.error('請設定尖峰與離峰時段');
      return;
    } else {
      patchValues.summer.peak.hours = summerPeakHours;
      patchValues.nonSummer.peak.hours = nonSummerPeakHours;
    }
    if (summerTime.length < 2) {
      Message.error('請設定夏季時間');
      return;
    } else {
      patchValues.summer.dates = [summerTime];
    }
    patchValues.chargeLimitMode === 'oneDayLimit'
      ? (patchValues.isSetTimeLimit = true)
      : (patchValues.isSetTimeLimit = false);

    if (patchValues.chargeLimitMode === 'periodLimit') {
      const newPeriodLimitSetting = patchValues.periodLimitSetting.map(
        (item) => {
          const startDate = moment(item.chargingStartTime).format('HH:mm'); // 解析 chargingStartTime
          const endDate = moment(item.chargingEndTime).format('HH:mm'); // 解析 chargingStartTime
          return {
            ...item,
            chargingStartTime: startDate,
            chargingEndTime: endDate,
            startHour: parseInt(startDate.split(':')[0]), // 取得 UTC 小時
            startMinute: parseInt(startDate.split(':')[1]), // 取得 UTC 分鐘
            endHour: parseInt(endDate.split(':')[0]), // 取得 UTC 小時
            endMinute: parseInt(endDate.split(':')[1]), // 取得 UTC
          };
        }
      );
      patchValues.periodLimitSetting = newPeriodLimitSetting;
    }

    try {
      if (initialValues?._id) {
        await client
          .service('chargingPileElectricityRate')
          .patch(initialValues._id, patchValues);
      } else {
        await client.service('chargingPileElectricityRate').create(patchValues);
      }
      const result = await Swal.fire({
        title: `確定修改充電樁設定？`,
        text: '送出後將會影響電費計價標準!!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#BEBEBE',
        confirmButtonText: '確定',
        cancelButtonText: '取消',
      });
      if (result.isConfirmed) {
        try {
          await axios(`/ezcon/api/chargingPile/active`, {
            method: 'GET',
            headers: { authorization: window.localStorage['feathers-jwt'] },
          }).then((res) => {
            if (res.data.canUpdate === false) {
              Swal.fire('失敗!', '有充電樁充電中，無法修改設定', 'error');
              return;
            } else {
              Swal.fire('成功!', '設定成功', 'success');
              handleClose();
              setTrigger({});
            }
          });
        } catch (e) {
          Message.error(e.response.data.message);
        }
      }
    } catch (e) {
      console.error(e);
      Message.error('設定失敗');
    }
  };
  return (
    <Box>
      <div style={{ overflowY: 'auto', height: '500px' }}>
        <Form
          form={form}
          onFinish={handleSubmit}
          onFinishFailed={(error) => {
            Message.error(error.errorFields[0].errors[0]);
          }}
          initialValues={initialValues}
          labelCol={{ span: 10 }}
          labelAlign="left"
          wrapperCol={{ span: 24 }}
        >
          <Tabs defaultActiveKey="1">
            <TabPane forceRender tab="充電樁基本設定" key="1">
              <Box sx={{ height: '500px', width: '500px' }}>
                <Item
                  label="待機電流(安培)"
                  name="standbyCurrent"
                  rules={[{ required: true, message: '請輸入待機電流' }]}
                >
                  <InputNumber min={0} />
                </Item>
                <Item
                  label="無訊號設定(分鐘)"
                  name="isVoltageMinutes"
                  rules={[{ required: true, message: '請輸入無訊號設定' }]}
                >
                  <InputNumber min={0} />
                </Item>
                <Item
                  label="移除充電時間設定(分鐘)"
                  name="removeChargingArrayMinutes"
                  rules={[
                    { required: true, message: '請輸入移除充電時間設定' },
                  ]}
                >
                  <InputNumber min={1} step={1} />
                </Item>

                <Item
                  label="超過電流及溫度關閉充電樁設定"
                  rules={[{ required: false }]}
                ></Item>
                <Item
                  label="電流超過設定(安培)"
                  name={['chargeLimitSetting', 'currentLimitA']}
                  rules={[{ required: true, message: '請輸入電流' }]}
                >
                  <InputNumber min={0} step={1} />
                </Item>

                <Item
                  label="溫度超過設定(℃)"
                  name={['chargeLimitSetting', 'temperatureMaxC']}
                  rules={[{ required: true, message: '請輸入溫度' }]}
                >
                  <InputNumber min={0} step={1} />
                </Item>
                <Space align="baseline" size={20}>
                  <Item
                    label="電流"
                    name={['chargeLimitSetting', 'allOfTrip', 'currentLimitA']}
                    rules={[{ required: false, message: '請輸入電流' }]}
                  >
                    <InputNumber min={0} step={1} />
                  </Item>

                  <Item
                    label="及溫度"
                    name={[
                      'chargeLimitSetting',
                      'allOfTrip',
                      'temperatureMaxC',
                    ]}
                    rules={[{ required: false, message: '請輸入溫度' }]}
                  >
                    <InputNumber min={0} step={1} />
                  </Item>
                </Space>

                <Item
                  label="系統異常每度費用(元/度)"
                  name={['systemShutDownElectricityRate']}
                  rules={[
                    { required: true, message: '請輸入系統異常每度費用' },
                  ]}
                >
                  <InputNumber min={0} />
                </Item>
                <Item
                  label="手動控制充電樁"
                  name="manualControl"
                  valuePropName="checked"
                  initialValue={false}
                  rules={[{ required: true, message: '請輸入手動控制充電樁' }]}
                >
                  <Switch />
                </Item>
                <Item
                  label="上限充電模式"
                  name="chargeLimitMode"
                  initialValue={false}
                  rules={[{ required: true, message: '請選擇上限充電模式' }]}
                >
                  <Radio.Group>
                    <Radio id="normal" value="normal">
                      未啟用(預設)
                    </Radio>
                    <Radio id="oneDayLimit" value="oneDayLimit">
                      一天上限
                    </Radio>
                    <Radio id="periodLimit" value="periodLimit">
                      特定時段上限
                    </Radio>
                  </Radio.Group>
                </Item>
                {selectedValue === 'oneDayLimit' && (
                  <>
                    <Item
                      label="最高上限充電時間(分鐘)"
                      name="chargingTimeLimitMins"
                      rules={[
                        { required: true, message: '請輸入最高上限充電時間' },
                      ]}
                    >
                      <InputNumber min={1} />
                    </Item>
                    <Item
                      label="每天重置時間(小時:分鐘)"
                      name="chargingTimeLimitResetTime"
                      rules={[
                        { required: true, message: '請輸入每天重置時間' },
                      ]}
                    >
                      <TimePicker format="HH:mm" minuteStep={30} />
                    </Item>
                  </>
                )}

                {selectedValue === 'periodLimit' && (
                  <Form.List name="periodLimitSetting">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Space.Compact direction="vertical" key={key}>
                            {key + 1}.
                            <Form.Item
                              {...restField}
                              label="最高上限(分鐘)"
                              name={[name, 'limitMinute']}
                              rules={[
                                {
                                  required: true,
                                },
                              ]}
                            >
                              <InputNumber
                                min={1}
                                placeholder="最高上限充電時間"
                              />
                            </Form.Item>
                            <Space
                              key={key}
                              align="baseline"
                              style={{ display: 'flex', marginBottom: 8 }}
                            >
                              <Form.Item
                                {...restField}
                                label="開始時間"
                                name={[name, 'chargingStartTime']}
                                rules={[
                                  { required: true, message: '請輸入開始時間' },
                                ]}
                              >
                                <TimePicker format="HH:mm" minuteStep={30} />
                              </Form.Item>

                              <Form.Item
                                {...restField}
                                label="結束時間"
                                name={[name, 'chargingEndTime']}
                                rules={[
                                  { required: true, message: '請輸入結束時間' },
                                ]}
                              >
                                <TimePicker format="HH:mm" minuteStep={30} />
                              </Form.Item>

                              {/* 刪除按鈕 */}
                              <MinusCircleOutlined
                                onClick={() => remove(name)}
                              />
                            </Space>
                          </Space.Compact>
                        ))}
                        {/* 新增一組數據 */}
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                        >
                          新增一組充電時間
                        </Button>
                      </>
                    )}
                  </Form.List>
                )}
              </Box>
            </TabPane>
            <TabPane forceRender tab="充電樁費用設定" key="2">
              <Box sx={{ height: '500px', width: '500px' }}>
                <Item
                  label="夏季尖峰費用(元/度)"
                  name={['summer', 'peak', 'price']}
                  rules={[{ required: true, message: '請輸入夏季尖峰費用' }]}
                >
                  <InputNumber min={0} />
                </Item>
                <Item
                  label="夏季離峰費用(元/度)"
                  name={['summer', 'offPeak', 'price']}
                  rules={[{ required: true, message: '請輸入夏季離峰費用' }]}
                >
                  <InputNumber min={0} />
                </Item>
                <Item
                  label="夏季基本電費(元)"
                  name={['summer', 'basicPrice']}
                  rules={[{ required: false }]}
                >
                  <InputNumber min={0} />
                </Item>
                <Item
                  label="非夏季尖峰費用(元/度)"
                  name={['nonSummer', 'peak', 'price']}
                  rules={[{ required: true, message: '請輸入非夏季尖峰費用' }]}
                >
                  <InputNumber min={0} />
                </Item>
                <Item
                  label="非夏季離峰費用(元/度)"
                  name={['nonSummer', 'offPeak', 'price']}
                  rules={[{ required: true, message: '請輸入非夏季離峰費用' }]}
                >
                  <InputNumber min={0} />
                </Item>
                <Item
                  label="非夏季基本電費(元)"
                  name={['nonSummer', 'basicPrice']}
                  rules={[{ required: false }]}
                >
                  <InputNumber min={0} />
                </Item>
              </Box>
            </TabPane>
            <TabPane forceRender tab="夏季時間設定" key="3">
              <Box
                sx={{
                  height: '500px',
                  width: '500px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <SummerRange
                  summerTime={summerTime}
                  setSummerTime={setSummerTime}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    夏季尖峰時段
                  </Typography>
                  <RangeSetting
                    ranges={summerPeakHours}
                    setRanges={setSummerPeakHours}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '14px',
                    }}
                  >
                    非夏季尖峰時段
                  </Typography>
                  <RangeSetting
                    ranges={nonSummerPeakHours}
                    setRanges={setNonSummerPeakHours}
                  />
                </Box>
              </Box>
            </TabPane>
          </Tabs>
        </Form>
      </div>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
        }}
      >
        <Button type="primary" onClick={form.submit}>
          確定
        </Button>
        <Button
          type="primary"
          warning
          onClick={() => {
            handleClose();
          }}
        >
          取消
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(SettingForm);
